import { ViteReactSSG } from 'vite-react-ssg';
import { routes } from './routes';
import { setLanguageFromPath, loadLanguageReport, namespacesBlockingHydration } from './config/i18n';
import { urlLangOf } from './config/langPrefixes';
import { installGlobalErrorHandlers, reportError } from './lib/errorReporter';
import { installAndroidBackButton, installNativeDownloadBridge, installNativeLinkPolicy, isNativeApp } from './lib/nativeApp';
import { isNativeAppPage } from './components/BrandedHostGuard';
import { startStoreEntitlementSync } from './lib/storePurchases';
import { isChunkLoadError, recoverFromChunkError, purgeServiceWorker } from './lib/chunkLoadError';
import { capturePrerenderSnapshot, hasPrerenderForCurrentPath } from './lib/prerenderSnapshot';
import { initGA, grantAnalyticsConsent } from './lib/analytics';
import { initPixel, grantPixelConsent } from './lib/metaPixel';
import { hasConsentedToCookies } from './components/CookieConsent';
import './index.css';
import 'flag-icons/css/flag-icons.min.css';

/** A stored Supabase session: someone who uses the app, not a passing visitor. */
function hasStoredSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) return true;
    }
  } catch { /* storage blocked: treat as a visitor */ }
  return false;
}

// Capture ?perf=1 HERE, in the entry, before anything else runs. The router
// normalises the designer's query string and drops unknown params, and
// `perfDiag` lives in a lazily-loaded chunk — so by the time it could read
// `location.search` the flag is already gone. Stash it on the window (and in
// localStorage, so a reload keeps it) and perfDiag picks it up from there.
if (typeof window !== 'undefined') {
  try {
    const p = new URLSearchParams(window.location.search).get('perf');
    if (p === '1' || p === 'true') {
      (window as unknown as { __PERF__?: boolean }).__PERF__ = true;
      window.localStorage.setItem('cctvPerf', '1');
    } else if (p === '0' || p === 'false') {
      window.localStorage.removeItem('cctvPerf');
    }
  } catch { /* private mode */ }
}

// Install global error/rejection handlers (client-side only)
if (typeof window !== 'undefined') {
  // Snapshot the server-rendered #root BEFORE anything can mutate it. If a
  // chunk fetch fails later, RootErrorBoundary restores these bytes instead of
  // showing the error screen — see src/lib/prerenderSnapshot.ts for why the
  // canonical link, not #root's non-emptiness, decides "prerendered".
  capturePrerenderSnapshot();
  installGlobalErrorHandlers();
  // Inside the iOS/Android shell, file exports go to the share sheet.
  installNativeDownloadBridge();
  // Store app only: re-apply the App Store / Google Play subscriptions this device holds.
  startStoreEntitlementSync();
  installNativeLinkPolicy(isNativeAppPage);
  installAndroidBackButton();

  // Chunk-load recovery. A lazy-loaded route module can fail to fetch when:
  //  - the user's session predates the latest deploy (old index.html still
  //    references chunk hashes that no longer exist on the server)
  //  - the user's network drops mid-request (common on mobile)
  //  - a CDN edge briefly 404s a fresh asset
  // Without recovery the user sees a blank screen or "Failed to fetch
  // dynamically imported module" toast. Purge the service worker and
  // hard-reload: the fresh index.html points at current chunks and the
  // navigation retries. Budget, storage-safety and purge all live in
  // src/lib/chunkLoadError, shared with both error boundaries so no path can
  // spend an attempt the others cannot see. Reported patterns:
  // err_mpevvnn7_c1ftdt (designer chunk, mobile, 2026-05-21),
  // err_ms3ghku0_1pxzol (dashboard, 2026-07-27), err_ms48fr4u_ibwsvo
  // (designer, 2026-07-28).
  //
  // Retrying the import in place instead of reloading was tried and removed:
  // the browser module map caches the failure, so only a re-import under a
  // different URL reaches the network, and the URL has to be scraped from the
  // error message. Measured in real engines: WebKit names no URL at all, and
  // Firefox names the failing static DEPENDENCY rather than the module that was
  // imported, so the retry resolves successfully with the WRONG module and
  // React renders it. A silent wrong page is worse than this reload.
  const handleChunkError = (err: unknown) => {
    // Shared matcher (src/lib/chunkLoadError). It was Chrome/Safari-only, so
    // Firefox ("error loading dynamically imported module") and stale-export
    // ("Importing binding name") preload/import rejections escaped to a
    // white screen instead of the recovery reload.
    if (!isChunkLoadError(err)) {
      return false;
    }
    // A page that arrived prerendered for this exact path never reloads over a
    // chunk failure: the server HTML is a guaranteed-good outcome and
    // RootErrorBoundary restores it. Reloading here re-downloads every asset —
    // during the 2026-08-27 WRS render storm these silent reloads tripled the
    // very traffic that was causing the failures. Purge the SW quietly instead
    // (no network cost) so the NEXT navigation starts clean, and let the
    // rejection reach the router's boundary.
    if (hasPrerenderForCurrentPath()) {
      void purgeServiceWorker();
      return false;
    }
    return recoverFromChunkError(err);
  };
  // Vite fires this for <link rel="modulepreload"> failures. It carries the
  // error on `payload`, NOT `detail` (vite/dist/node/chunks/dep-*.js:
  // `const e = new Event("vite:preloadError", {cancelable:true}); e.payload = err`).
  // Reading `detail` yielded undefined and the `|| e` fallback then stringified
  // the Event itself to "[object Event]", which matches no pattern, so this
  // listener had never once fired.
  //
  // Deliberately NOT calling preventDefault(). Vite's helper is:
  //   .catch(err => { const e = new Event('vite:preloadError', {cancelable:true});
  //                   e.payload = err; dispatchEvent(e);
  //                   if (!e.defaultPrevented) throw err; })
  // so suppressing the default makes that catch fall off the end and RESOLVE
  // THE IMPORT WITH `undefined`. React.lazy then reads `undefined.default` and
  // throws "Cannot read properties of undefined (reading 'default')" from a
  // stack that names only minified React internals, which is what filled the
  // error log (118 reports in 24h, one incident each). Letting Vite rethrow
  // gives React a real rejection, so the boundary sees an honest chunk error.
  // The reload is already in flight either way; recoverFromChunkError is
  // idempotent per incident so the second report costs no extra budget.
  window.addEventListener('vite:preloadError', (e) => {
    const payload = (e as unknown as { payload?: unknown; detail?: unknown });
    handleChunkError(payload.payload ?? payload.detail ?? e);
  });
  // Runtime import() rejections that escape route Suspense boundaries.
  window.addEventListener('unhandledrejection', (e) => {
    if (handleChunkError(e.reason)) e.preventDefault();
  });

  // A chunk whose body is HTML (a 200 interstitial/error page cached under a
  // JS URL by a stale service worker, see public/sw.js) parses as a module
  // and throws SyntaxError "Unexpected token '<'". That surfaces as a window
  // 'error', not a rejection, so handleChunkError above never sees it, and a
  // plain reload would re-serve the poisoned chunk. recoverFromChunkError
  // purges the caches and the registration first, so the reload refetches.
  window.addEventListener('error', (e) => {
    const msg = String((e as ErrorEvent).message || '');
    if (!/Unexpected token '?<'?/.test(msg)) return;
    // Same prerender rule as handleChunkError above: keep the server HTML,
    // heal the poisoned cache in the background, skip the reload.
    if (hasPrerenderForCurrentPath()) {
      void purgeServiceWorker();
      return;
    }
    recoverFromChunkError();
  });

  // GA4 + Meta Pixel init. Both default to consent-denied so the loaders run
  // (allowing instant fire after grant) but no cookies/IDs are set until the
  // user accepts the cookie banner. PageView fires from <RouteTracker> on
  // every route change for both providers.
  // The store app loads neither: no tracking, so no App Tracking Transparency.
  if (!isNativeApp()) {
    initGA();
    initPixel();
    // Restore granted consent from a prior session (localStorage).
    if (hasConsentedToCookies()) {
      grantAnalyticsConsent();
      grantPixelConsent();
    }
  }

  // Register service worker for static asset caching (production only).
  // Vendor chunks (cameras-db 1.4MB, vendor-three 996KB, vendor-pdf 889KB)
  // become instant on second visit. SW versioning is in /sw.js — bumped per release.
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    const registerSw = () => {
      // updateViaCache:'none' makes the browser bypass the HTTP cache when
      // checking sw.js for updates — defense in depth against a CDN/host that
      // pins the SW script (Cloudflare served a 21-day-old sw.js as immutable;
      // see public/.htaccess), so a new SW is picked up on the next visit.
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {
        // Silent fail — SW is progressive enhancement, not critical.
      });
    };
    // Prerendered pages start this script after their first paint
    // (scripts/ssg-postprocess.mjs), which can be after `load` has fired.
    if (document.readyState === 'complete') registerSw();
    else window.addEventListener('load', registerSw);

    // When a new SW with skipWaiting + clients.claim takes over mid-session,
    // the active page is still wired to the old asset URLs. Without a hard
    // reload we get a vendor-chunk mismatch the moment the user triggers any
    // dynamic import (Stripe Checkout, PDF export, three.js V3 designer).
    // First-time SW registration also fires this event — use the
    // `hasController` flag to skip that case so the very first visit doesn't
    // self-reload.
    let hasController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hasController) {
        window.location.reload();
      } else {
        hasController = true;
      }
    });
  }
}

// Extract language from a path (same logic as setLanguageFromPath but returns the lang)
const extractLang = urlLangOf;

export const createRoot = ViteReactSSG(
  {
    routes,
    // vite-react-ssg defaults v7_startTransition to TRUE, which wraps every
    // router navigation in React.startTransition. React then keeps the OLD
    // page mounted (no Suspense fallback) while a lazy route chunk
    // downloads — so clicking a CTA into the designer (~MBs on slow mobile)
    // showed zero feedback for 10s+ and the button felt dead. Opting out
    // commits the navigation immediately, letting RootLayout's branded
    // Suspense fallback paint for the duration of the chunk fetch.
    future: { v7_startTransition: false },
  },
  async (context) => {
    // Determine which language this page needs
    const pathname = context.routePath || (context.isClient ? window.location.pathname : '/');
    const lang = extractLang(pathname);

    // Load language resources (async, works in both SSG/Node and client)
    // English is always available; non-English languages are loaded on demand.
    if (lang !== 'en') {
      const reasons: Record<string, string> = {};
      const failed = await loadLanguageReport(lang, reasons);
      const blocking = namespacesBlockingHydration(failed);
      if (blocking.length > 0 && context.isClient && hasPrerenderForCurrentPath()) {
        // Hydrating now would swap every string of a missing namespace for its
        // English fallback over the server's translated HTML, and Google files
        // that DOM as a duplicate of the EN page ("Duplicate, Google chose
        // different canonical", GSC 2026-09-26). Keep the server page, the same
        // rule RootErrorBoundary applies to a failed route chunk.
        void reportError(new Error(`locale ${lang} incomplete: ${blocking.join(',')}`), {
          component: 'i18n-prerender-kept',
          lang,
          failed,
          reasons,
        });
        await new Promise<never>(() => {});
      }
      // Only the signed-in app's namespace missing, for someone with no stored
      // session: nothing on this page uses it, and a crawler that closed each
      // page while that chunk was still loading sent ~1 900 of these a day
      // (digest 2026-09-29). Anyone signed in, or any other namespace, reports.
      const unused = blocking.length === 0 && !hasStoredSession();
      if (failed.length > 0 && context.isClient && !unused) {
        // The page hydrates. The chunk URL is cut from the message so that
        // every locale lands in one digest row with its cause.
        const cause = (reasons[failed[0]] ?? '').replace(/:?\s*https?:\/\/\S+/g, '').trim();
        void reportError(new Error(`i18n namespace ${failed.join(',')} not loaded: ${cause}`), {
          component: 'i18n-namespace-failed',
          lang,
          failed,
          reasons,
        });
      }
    }

    // Set the language synchronously on the i18n instance (resources are now available)
    setLanguageFromPath(pathname);
  },
);
